import {
  geojsonTypes,
  cursors,
  types,
  modes,
  events,
  activeStates
} from "@mapbox/mapbox-gl-draw/src/constants";
import {
  isEscapeKey,
  isEnterKey
} from "@mapbox/mapbox-gl-draw/src/lib/common_selectors";

const FixedRectangleMode = {};

const DEFAULT_SIZE_IN_METER = {
  width: 30 * 1000, // 30km
  height: 20 * 1000
};

function degrees2meters(lon, lat) {
  const x = (lon * 20037508.34) / 180;
  let y = Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / (Math.PI / 180);
  y = (y * 20037508.34) / 180;
  return [x, y];
}

function meters2degress(x, y) {
  const lon = (x * 180) / 20037508.34;
  const lat =
    (Math.atan(Math.exp((y * Math.PI) / 20037508.34)) * 360) / Math.PI - 90;
  return [lon, lat];
}

function createPolygonCoordinates(center, { width, height }) {
  const [x, y] = degrees2meters(center[0], center[1]);

  const min = meters2degress(x - width / 2, y - height / 2);
  const max = meters2degress(x + width / 2, y + height / 2);

  return [
    [
      [min[0], max[1]],
      [max[0], max[1]],
      [max[0], min[1]],
      [min[0], min[1]],
      [min[0], max[1]]
    ]
  ];
}

FixedRectangleMode.onSetup = function(opts) {
  const polygon = this.newFeature({
    type: geojsonTypes.FEATURE,
    properties: {},
    geometry: {
      type: geojsonTypes.POLYGON,
      coordinates: [[]]
    }
  });

  this.addFeature(polygon);

  this.clearSelectedFeatures();
  this.updateUIClasses({ mouse: cursors.ADD });
  this.activateUIButton(types.POLYGON);
  this.setActionableState({
    trash: true
  });

  return {
    initialSizeInMeters: opts.initialSize || DEFAULT_SIZE_IN_METER,
    polygon
  };
};

FixedRectangleMode.stopDrawingAndRemove = function(state) {
  this.deleteFeature([state.polygon.id], { silent: true });
  this.changeMode(modes.SIMPLE_SELECT);
};

FixedRectangleMode.onTap = FixedRectangleMode.onClick = function(state, e) {
  this.updateUIClasses({ mouse: cursors.MOVE });

  const center = [e.lngLat.lng, e.lngLat.lat];

  const polygonCoordinates = createPolygonCoordinates(
    center,
    state.initialSizeInMeters
  );

  state.polygon.incomingCoords(polygonCoordinates);

  this.map.fire(events.CREATE, {
    features: [state.polygon.toGeoJSON()]
  });
  this.changeMode(modes.SIMPLE_SELECT, { featureIds: [state.polygon.id] });
};

FixedRectangleMode.onStop = function(state) {
  this.activateUIButton();
  if (!state.polygon.isValid()) {
    this.deleteFeature([state.polygon.id], { silent: true });
  }
};

FixedRectangleMode.onMouseMove = function(state, e) {
  const center = [e.lngLat.lng, e.lngLat.lat];

  const polygonCoordinates = createPolygonCoordinates(
    center,
    state.initialSizeInMeters
  );

  state.polygon.incomingCoords(polygonCoordinates);
};

FixedRectangleMode.toDisplayFeatures = function(state, geojson, display) {
  // Never render the polygon we're drawing
  const isActivePoint = geojson.properties.id === state.polygon.id;
  geojson.properties.active = isActivePoint
    ? activeStates.ACTIVE
    : activeStates.INACTIVE;
  if (!isActivePoint) return display(geojson);

  if (geojson.geometry.coordinates.length === 0) return;

  if (!geojson.geometry.coordinates[0][0]) return;

  display(geojson);
};

FixedRectangleMode.onTrash = FixedRectangleMode.stopDrawingAndRemove;

FixedRectangleMode.onKeyUp = function(state, e) {
  if (isEscapeKey(e) || isEnterKey(e)) {
    return this.stopDrawingAndRemove(state, e);
  }
};

export { FixedRectangleMode };
